import React, { createContext, useCallback, useState, useContext, useEffect } from 'react';
import { setGetAccessTokenSilently, setOnAuthFailure, getTokenViaAuth0, clearCachedToken, getInitialPayload } from '../services/api';
import { useQuery as useConvexQuery, useMutation, useConvexAuth } from "convex/react";
import { useAuth0, AppState } from "@auth0/auth0-react";
import { User } from '../types/user';
import { ModelRegistry } from '../types/model';
import { PayloadVoice } from '../types/api';
import { RemainingResourceCount } from '../types/usage';
import { Subscription } from '../types/subscription';
import { Mode } from '../types/mode';
import { useAnalytics } from '../hooks/useAnalytics';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  login: (options?: { appState?: AppState }) => void;
  logout: () => void;
  isLoading: boolean;
  isDataLoading: boolean;
  updateMemory: (newMemoryArray: any[]) => Promise<void>;
  refetchInitialData: () => void;
  updateUser: (newUserData: Partial<User>) => void;
  imageUsageData: RemainingResourceCount | null;
  chatUsageData: RemainingResourceCount | null;
  reduceUsageData: (newCount: number | null) => void;
  avatar: string | null;
  isUltimate: boolean;
  modelRegistry: ModelRegistry | null;
  increaseImageGenerationCount: (num: number) => void;
  connectGoogleDrive: () => void;
  checkUsageData: () => boolean;
  voices: PayloadVoice[];
  subscription: Subscription | null;
  isSubscriptionLoading: boolean;
  updateUserModes: (modes: Mode[], id: string) => Promise<void>;
  billingIssue: boolean; // <-- Added billingIssue to context type
  /** True after `/users/initial-payload` has been fetched (success or error) for this session. */
  initialPayloadLoaded: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const GUEST_CHATS_LIMIT = 5;

const getGuestChatsFromStorage = (): number => {
  const stored = localStorage.getItem('guestRemainingChats');
  const lastReset = localStorage.getItem('guestChatsLastReset');
  const today = new Date().toDateString();
  if (!lastReset || lastReset !== today) {
    localStorage.setItem('guestRemainingChats', GUEST_CHATS_LIMIT.toString());
    localStorage.setItem('guestChatsLastReset', today);
    return GUEST_CHATS_LIMIT;
  }
  return stored ? parseInt(stored, 10) : GUEST_CHATS_LIMIT;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const { isAuthenticated, isLoading: auth0Loading, logout: Auth0Logout, loginWithRedirect, user: auth0User, getAccessTokenSilently } = useAuth0();
  const { identify } = useAnalytics();
  const [user, setUser] = useState<User | null>(null);
  const [isProfileLoading, setIsProfileLoading] = useState<boolean>(true);
  const [isUltimate, setIsUltimate] = useState<boolean>(false);
  const [modelRegistry, setModelRegistry] = useState<ModelRegistry | null>(null);
  const [voices, setVoices] = useState<PayloadVoice[]>([]);
  const [tokenReady, setTokenReady] = useState<boolean>(false);
  const [initialPayloadLoaded, setInitialPayloadLoaded] = useState<boolean>(false);
  const [imageUsageData, setImageUsageData] = useState<RemainingResourceCount | null>(null);
  const [chatUsageData, setChatUsageData] = useState<RemainingResourceCount | null>(null);

  // Billing issue state
  const [billingIssue, setBillingIssue] = useState<boolean>(false);

  // Subscription loading state
  const [isSubscriptionLoading, setIsSubscriptionLoading] = useState<boolean>(false);

  // Convex rejects calls until it holds the Auth0 token, which lands a moment
  // after Auth0 reports the user signed in. Every Convex query hangs off the
  // user loaded here, so gating this one gates them all.
  const { isAuthenticated: convexAuthenticated } = useConvexAuth();

  // Get user from Convex using email
  const convexUser: User | null = useConvexQuery(
    "users:getByEmail" as any,
    isAuthenticated && convexAuthenticated && !auth0Loading && !!auth0User?.email && tokenReady ? { 
      email: auth0User.email 
    } : "skip"
  );

  // Get active subscription from Convex using userId
  const convexSubscription: Subscription | null | undefined = useConvexQuery(
    "subscriptions:getActiveByUser" as any,
    isAuthenticated && convexAuthenticated && !auth0Loading && convexUser?._id && tokenReady ? {
      userId: convexUser._id
    } : "skip"
  );

  // Set subscription loading state and billing issue based on subscription status
  useEffect(() => {
    // Convex queries return undefined while loading, null when no data, or the data when ready
    if (isAuthenticated && convexUser?._id && tokenReady) {
      if (convexSubscription === undefined) {
        setIsSubscriptionLoading(true);
      } else {
        setIsSubscriptionLoading(false);
        
        // Update user properties with subscription info when loaded
        if (convexSubscription && convexUser._id) {
          identify(convexUser._id, {
            plan: convexSubscription.plan,
            subscription_status: convexSubscription.status,
            subscription_interval: convexSubscription.interval,
            trial_end: convexSubscription.trialEnd || undefined
          });
        }
      }
    } else {
      setIsSubscriptionLoading(false);
    }

    if (convexSubscription && convexSubscription.status) {
      if (convexSubscription.status === 'past_due') {
        setBillingIssue(true);
      } else {
        setBillingIssue(false);
      }
    } else {
      setBillingIssue(false);
    }
  }, [convexSubscription, isAuthenticated, convexUser?._id, tokenReady, identify, convexUser]);

  // Convex mutation for updating user
  const updateUserMutation = useMutation("users:updateUser" as any);

  // Update user state when convex user changes
  useEffect(() => {
    if (convexUser) {
      setUser(convexUser);
      setIsProfileLoading(false);
      
      // Track user login/identification
      if (convexUser._id) {
        identify(convexUser._id, {
          email: convexUser.email || undefined,
          name: convexUser.name || undefined,
          created_at: convexUser.createdAt || undefined,
          legacy_id: convexUser.legacyId || undefined
        });
      }
    } else if (convexUser === null && isAuthenticated && !auth0Loading && tokenReady) {
      setUser(null); // Explicitly set to null instead of leaving undefined
      setIsProfileLoading(false);
    } else if (!auth0Loading && !isAuthenticated) {
      // Guest / logged-out path: Convex query is skipped, so the convexUser
      // branches above never fire. Without this, isProfileLoading stays true
      // forever and any consumer gating on isDataLoading hangs.
      setUser(null);
      setIsProfileLoading(false);
    }
  }, [convexUser, isAuthenticated, auth0Loading, tokenReady, identify]);

  const login = useCallback((options?: { appState?: AppState }) => {
    loginWithRedirect({
      ...options,
      appState: {
        ...options?.appState,
        returnTo: options?.appState?.returnTo || window.location.origin
      }
    });
  }, [loginWithRedirect]);

  const logout = useCallback(() => {
    // Clear cached tokens
    clearCachedToken();
    
    // Reset all user state
    setUser(null);
    setIsUltimate(false);
    setModelRegistry(null);
    setVoices([]);
    setIsProfileLoading(true);
    setTokenReady(false);
    setInitialPayloadLoaded(false);
    
    // Clear app-specific localStorage keys (Auth0 handles its own domain cleanup)
    const appStorageKeys = [
      'chat_',
      'draft_',
      'file_',
      'pinnedChats',
      'guestRemainingChats',
      'guestChatsLastReset',
      'auth0.',
      '@@user@@'
    ];
    
    Object.keys(localStorage).forEach(key => {
      if (appStorageKeys.some(prefix => key.startsWith(prefix) || key === prefix || key.includes(prefix))) {
        localStorage.removeItem(key);
      }
    });
    
    // Clear app-specific sessionStorage keys (preserve other app features)
    const sessionKeys = Object.keys(sessionStorage);
    sessionKeys.forEach(key => {
      if (appStorageKeys.some(prefix => key.startsWith(prefix) || key === prefix || key.includes(prefix))) {
        sessionStorage.removeItem(key);
      }
    });
    
    // Auth0 logout handles its own domain cleanup (*.auth0.com cookies, etc.)
    Auth0Logout({
      logoutParams: {
        returnTo: window.location.origin
      }
    });

  }, [Auth0Logout]);

  useEffect(() => {
    setOnAuthFailure(() => { logout(); });
  }, [logout]);

  const updateMemory = useCallback(async (newMemoryArray: string[]): Promise<void> => {
    try {
      const memoryString = JSON.stringify(newMemoryArray);
      if (user && user._id) {
        await updateUserMutation({
          id: user._id,
          preferences: memoryString
        });
      }
    } catch (error) {
      throw error;
    }
  }, [user, updateUserMutation]);

  const updateUser = useCallback(async (newUserData: Partial<User>) => {
    const { name, s_name, s_level } = newUserData;
    let mutationPayload: any = {};

    if (user && user._id && (name !== undefined || s_name !== undefined || s_level !== undefined)) {
      if (name !== undefined) mutationPayload.name = name;
      if (s_name !== undefined) mutationPayload.s_name = s_name;
      if (s_level !== undefined) mutationPayload.s_level = s_level;

      try {
        await updateUserMutation({
          id: user._id,
          ...mutationPayload
        });
      } catch (error) {
        console.error('Error updating user via mutation:', error);
      }
    }
  }, [user, updateUserMutation]);

  const updateUserModes = useCallback(async (modes: Mode[], id: string): Promise<void> => {
    try {
      const modesString = JSON.stringify(modes);
      await updateUserMutation({
        id: id,
        modes: modesString
      });
    } catch (error) {
      console.error('Error updating user modes:', error);
      throw error;
    }
  }, [updateUserMutation]);

  const reduceUsageData = useCallback((newUsedCount: number | null) => {
    if (isAuthenticated && chatUsageData ) {
      // For authenticated users, update usage data directly
      let used = chatUsageData.used;
      let remaining = chatUsageData.remaining;
      let total = chatUsageData.total;

      
      used = used + 1;
      remaining = Math.max(0, remaining - 1);
      

      setChatUsageData({
        used,
        remaining,
        total,
        cycleEndDate: new Date(new Date().setHours(23, 59, 59, 999)).toISOString()
      });
    } else if (chatUsageData) {
      // For guests, update localStorage and usage data
      let current = chatUsageData.remaining ?? GUEST_CHATS_LIMIT;
      let updatedRemaining: number;

      if (newUsedCount === null) {
        updatedRemaining = Math.max(0, current - 1);
      } else {
        updatedRemaining = Math.max(0, newUsedCount);
      }

      localStorage.setItem('guestRemainingChats', updatedRemaining.toString());

      setChatUsageData({
        used: GUEST_CHATS_LIMIT - updatedRemaining,
        remaining: updatedRemaining,
        total: GUEST_CHATS_LIMIT,
        cycleEndDate: new Date(new Date().setHours(23, 59, 59, 999)).toISOString()
      });
    }
  }, [isAuthenticated, chatUsageData]);

  const increaseImageGenerationCount = useCallback((num: number) => {
    setImageUsageData(prev => {
      if (!prev) return null;
      return {
        ...prev,
        used: prev.used + num,
        remaining: Math.max(0, prev.remaining - num),
      };
    });
  }, []);

  const connectGoogleDrive = useCallback(() => {
    const googleAuthUrl = `https://accounts.google.com/o/oauth2/v2/auth?` +
      `client_id=${process.env.REACT_APP_GOOGLE_CLIENT_ID}&` + // Use your Client ID
      `redirect_uri=${encodeURIComponent(window.location.origin + '/google-drive/callback')}&` +
      `response_type=code&` +
      `scope=${encodeURIComponent('https://www.googleapis.com/auth/drive.readonly')}&` +
      `access_type=offline&` +
      `prompt=consent`;

    window.location.href = googleAuthUrl; // Redirect user
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      const lastReset = localStorage.getItem('guestChatsLastReset');
      const today = new Date().toDateString();
      if (!lastReset || lastReset !== today) {
        const guestLimit = GUEST_CHATS_LIMIT;
        setChatUsageData({ 
          used: 0, 
          remaining: guestLimit, 
          total: guestLimit,
          cycleEndDate: new Date(new Date().setHours(23, 59, 59, 999)).toISOString()
        });
        localStorage.setItem('guestRemainingChats', guestLimit.toString());
        localStorage.setItem('guestChatsLastReset', today);
      } else {
        const stored = getGuestChatsFromStorage();
        setChatUsageData({ 
          used: GUEST_CHATS_LIMIT - stored, 
          remaining: stored, 
          total: GUEST_CHATS_LIMIT,
          cycleEndDate: new Date(new Date().setHours(23, 59, 59, 999)).toISOString()
        });
      }
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated && auth0User?.email) {
      setGetAccessTokenSilently(async () => {
        const auth0Token = await getAccessTokenSilently();
        return getTokenViaAuth0(auth0Token);
      });
      setTokenReady(true);
    } else {
      setTokenReady(false);
    }
  }, [auth0User, isAuthenticated, getAccessTokenSilently]);


  // Fetch initial payload for authenticated users
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        if (!(isAuthenticated && tokenReady && !initialPayloadLoaded)) return;
        const payload = await getInitialPayload();
        if (!mounted) return;
        setImageUsageData(payload.usage?.remainingImageGenerations ?? null);
        setChatUsageData(payload.usage?.remainingChats ?? null);
        setModelRegistry(payload.modelRegistry ?? null);
        setVoices(payload.voices ?? []);
        setInitialPayloadLoaded(true);
      } catch (e) {
        if (!mounted) return;
        console.error('Failed to fetch initial payload:', e);
        setInitialPayloadLoaded(true);
      }
    })();
    return () => { mounted = false; };
  }, [isAuthenticated, tokenReady, initialPayloadLoaded]);

  const checkUsageData = useCallback((): boolean => {
    const chatRemaining = chatUsageData?.remaining;
    if (
      (chatRemaining !== null && chatRemaining !== undefined && chatRemaining <= 0) ||
      auth0Loading ||
      (isAuthenticated && !user)
    ) {
      return false;
    }
    return true;
  }, [chatUsageData, auth0Loading, isAuthenticated, user]);

  // UsageData is now managed independently from user data


  const memoizedAuthValue = React.useMemo(() => ({
    user: user ?? null,
    isAuthenticated,
    login,
    logout,
    isLoading: auth0Loading,
    isDataLoading: isProfileLoading,
    updateMemory,
    refetchInitialData: () => {
      setInitialPayloadLoaded(false);
    },
    updateUser,
    imageUsageData,
    chatUsageData,
    reduceUsageData,
    avatar: auth0User?.picture || null,
    isUltimate,
    modelRegistry,
    increaseImageGenerationCount,
    connectGoogleDrive,
    checkUsageData,
    voices,
    subscription: convexSubscription ?? null,
    isSubscriptionLoading,
    updateUserModes,
    billingIssue, // <-- Add billingIssue to context value
    initialPayloadLoaded,
  }), [
    user,
    isAuthenticated,
    login,
    logout,
    auth0Loading,
    isProfileLoading,
    updateMemory,
    updateUser,
    imageUsageData,
    chatUsageData,
    reduceUsageData,
    auth0User,
    isUltimate,
    modelRegistry,
    increaseImageGenerationCount,
    connectGoogleDrive,
    checkUsageData,
    voices,
    convexSubscription,
    isSubscriptionLoading,
    updateUserModes,
    billingIssue, // <-- Add billingIssue to dependency array
    initialPayloadLoaded,
  ]);

  return (
    <AuthContext.Provider value={memoizedAuthValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
